import { useQuery } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import {
  assignSuggestedBidsToKeywords,
  buildWorkspaceSuggestedBidRequests,
  parseSbSuggestedBids,
  parseSpTargetBidRecommendations,
  type WorkspaceSuggestedBid,
  type WorkspaceSuggestedBidKeyword,
} from '@/utils/workspaceKeywordSuggestedBids';

const SUGGESTED_BID_STALE_MS = 30 * 60 * 1000;

/** Amazon suggested bids for the keyword rows on the current Workspace page. */
export function useWorkspaceKeywordSuggestedBids(args: {
  profileId: string;
  rows: WorkspaceSuggestedBidKeyword[];
  enabled: boolean;
}) {
  const keywordIds = args.rows
    .filter((row) => row.rowKind !== 'auto_target')
    .map((row) => row.keywordId)
    .sort();

  const query = useQuery({
    queryKey: ['workspace-keyword-suggested-bids', args.profileId, keywordIds],
    enabled: args.enabled && Boolean(args.profileId) && keywordIds.length > 0,
    staleTime: SUGGESTED_BID_STALE_MS,
    queryFn: async () => {
      const { sp, sb } = buildWorkspaceSuggestedBidRequests(args.rows);
      const byKeywordId = new Map<string, WorkspaceSuggestedBid>();
      await Promise.allSettled([
        ...sp.map(async (req) => {
          const res = await adsApi.getSpTargetBidRecommendations(args.profileId, req.body);
          if (!res.success) return;
          assignSuggestedBidsToKeywords(
            args.rows,
            req.keywordIds,
            parseSpTargetBidRecommendations(res.data),
            byKeywordId
          );
        }),
        ...sb.map(async (req) => {
          const res = await adsApi.getSbKeywordBidRecommendations(args.profileId, req.body);
          if (!res.success) return;
          assignSuggestedBidsToKeywords(args.rows, req.keywordIds, parseSbSuggestedBids(res.data), byKeywordId);
        }),
      ]);
      return byKeywordId;
    },
  });

  return {
    suggestedBidsByKeywordId: query.data ?? null,
    loading: query.isFetching && !query.data,
  };
}
